import { TRANSLATION_PROVIDERS, TRANSLATION_PROVIDER_LABELS } from '@sky-lingo-v2/shared';
import type { ProviderPublicConfig, TranslationProvider, TranslationPublicConfig } from '@sky-lingo-v2/shared';

export type CredentialKey = 'apiKey' | 'secretId' | 'secretKey' | 'accessKeyId' | 'accessKeySecret';
export type MetadataKey = 'model' | 'baseUrl' | 'region' | 'projectId';
type CredentialField = { key: CredentialKey; label: string; clear: string };
type MetadataField = { key: MetadataKey; label: string; placeholder: string; numeric?: boolean };
export type ProviderInput = Record<CredentialKey, string> & { clear: Record<CredentialKey, boolean> };
export const providerIds = [...TRANSLATION_PROVIDERS];
const apiKey: CredentialField = { key: 'apiKey', label: 'API Key', clear: 'clearApiKey' };
const secretId: CredentialField = { key: 'secretId', label: '腾讯 Secret ID', clear: 'clearSecretId' };
const secretKey: CredentialField = { key: 'secretKey', label: 'Secret Key', clear: 'clearSecretKey' };
const accessKeyId: CredentialField = { key: 'accessKeyId', label: 'AccessKey ID', clear: 'clearAccessKeyId' };
const accessKeySecret: CredentialField = { key: 'accessKeySecret', label: 'AccessKey Secret', clear: 'clearAccessKeySecret' };
const endpoint: MetadataField = { key: 'baseUrl', label: '完整接口地址', placeholder: '留空使用服务默认地址' };
const signedEndpoint: MetadataField = { key: 'baseUrl', label: 'API 根地址', placeholder: 'HTTPS 根地址，不含路径和参数；留空使用默认值' };
const model: MetadataField = { key: 'model', label: '模型名称', placeholder: '留空使用服务默认模型' };
export const providerDetails: Record<TranslationProvider, {
  label: string; initial: string; credentials: CredentialField[]; metadata: MetadataField[]; backup?: boolean;
}> = {
  google: { label: TRANSLATION_PROVIDER_LABELS.google, initial: 'G', credentials: [apiKey], metadata: [endpoint] },
  deepl: { label: TRANSLATION_PROVIDER_LABELS.deepl, initial: 'D', credentials: [apiKey], metadata: [endpoint] },
  hunyuan: { label: TRANSLATION_PROVIDER_LABELS.hunyuan, initial: 'H', credentials: [secretId, secretKey], metadata: [model, signedEndpoint] },
  qwen: { label: TRANSLATION_PROVIDER_LABELS.qwen, initial: 'Q', credentials: [apiKey], metadata: [model, endpoint] },
  ai: { label: TRANSLATION_PROVIDER_LABELS.ai, initial: 'AI', credentials: [accessKeyId, accessKeySecret], metadata: [signedEndpoint] },
  aai: { label: TRANSLATION_PROVIDER_LABELS.aai, initial: 'TX', credentials: [secretId, secretKey], metadata: [signedEndpoint,
    { key: 'region', label: '地域', placeholder: '留空使用默认地域' },
    { key: 'projectId', label: '项目 ID（0 为默认项目）', placeholder: '0', numeric: true } ] },
  index35b: { label: TRANSLATION_PROVIDER_LABELS.index35b, initial: 'Index', credentials: [], metadata: [], backup: true },
  bing: { label: TRANSLATION_PROVIDER_LABELS.bing, initial: 'Bing', credentials: [], metadata: [], backup: true },
  index2b: { label: TRANSLATION_PROVIDER_LABELS.index2b, initial: 'Index', credentials: [], metadata: [], backup: true },
};
export const primaryProviderIds = providerIds.filter(id => !providerDetails[id].backup);

export function defaultRegistrationProvider(providers: TranslationProvider[], current: TranslationProvider): TranslationProvider | null {
  const eligible = primaryProviderIds.filter(id => providers.includes(id));
  return eligible.includes(current) ? current : eligible[0] || null;
}

export function createProviderInputs(): Record<TranslationProvider, ProviderInput> {
  return Object.fromEntries(providerIds.map(id => [id, {
    apiKey: '', secretId: '', secretKey: '', accessKeyId: '', accessKeySecret: '',
    clear: { apiKey: false, secretId: false, secretKey: false, accessKeyId: false, accessKeySecret: false },
  }])) as Record<TranslationProvider, ProviderInput>;
}

export function credentialHint(config: ProviderPublicConfig, key: CredentialKey): string {
  return key === 'apiKey' ? config.apiKeyHint : config.credentialHints?.[key] || '';
}

export function buildTranslationUpdate(config: TranslationPublicConfig, inputs: Record<TranslationProvider, ProviderInput>) {
  const providers = Object.fromEntries(providerIds.map(id => {
    const current = config.providers[id];
    const input = inputs[id];
    const update: Record<string, unknown> = { enabled: current.enabled };
    for (const field of providerDetails[id].credentials) {
      if (input.clear[field.key]) update[field.clear] = true;
      else if (input[field.key].trim()) update[field.key] = input[field.key].trim();
    }
    for (const field of providerDetails[id].metadata) {
      const value = current[field.key];
      if (field.numeric) {
        if (value !== undefined && value !== null && value !== '') {
          const number = Number(value);
          if (!Number.isSafeInteger(number) || number < 0) throw new Error('invalid_project_id');
          update[field.key] = number;
        }
      } else update[field.key] = typeof value === 'string' ? value.trim() : '';
    }
    if (id === 'deepl' && current.modelType) update.modelType = current.modelType;
    return [id, update];
  }));
  // Routing is managed independently; saving model settings preserves its latest server values.
  return { providers };
}
